import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import './styles/global.css';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ChatBubble from './components/common/ChatBubble';
import DepositButton from './components/common/DepositButton';
import AnnouncementBanner from './components/common/AnnouncementBanner';

import Home from './pages/Home';
import Platforms from './pages/Platforms';
import Offers from './pages/Offers';
import About from './pages/About';
import Memberships from './pages/Memberships';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Games from './pages/Games';
import ChatPage from './pages/Chat';
import Reviews from './pages/Reviews';
import Deposit from './pages/Deposit';
import PolicyPage from './pages/PolicyPage';
import WheelPage from './pages/WheelPage';

import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPlatforms from './pages/admin/AdminPlatforms';
import AdminOffers from './pages/admin/AdminOffers';
import AdminMemberships from './pages/admin/AdminMemberships';
import AdminAbout from './pages/admin/AdminAbout';
import AdminChat from './pages/admin/AdminChat';
import AdminReviews from './pages/admin/AdminReviews';
import AdminPayments from './pages/admin/AdminPayments';
import AdminDeposits from './pages/admin/AdminDeposits';
import AdminGames from './pages/admin/AdminGames';
import AdminPolicy from './pages/admin/AdminPolicy';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminReferrals from './pages/admin/AdminReferrals';
import AdminWheelSpins from './pages/admin/AdminWheelSpins';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner"/></div>;
  return user ? children : <Navigate to="/login"/>;
};
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login"/>;
  if (!isAdmin) return <Navigate to="/"/>;
  return children;
};
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-spinner"><div className="spinner"/></div>;
  return user ? <Navigate to="/"/> : children;
};

const MainLayout = ({ children }) => (
  <>
    <Navbar/>
    <AnnouncementBanner/>
    <main style={{ minHeight: 'calc(100vh - 70px)' }}>{children}</main>
    <Footer/>
    <ChatBubble/>
    <DepositButton/>
  </>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/"             element={<MainLayout><Home/></MainLayout>}/>
      <Route path="/platforms"    element={<MainLayout><Platforms/></MainLayout>}/>
      <Route path="/offers"       element={<MainLayout><Offers/></MainLayout>}/>
      <Route path="/about"        element={<MainLayout><About/></MainLayout>}/>
      <Route path="/memberships"  element={<MainLayout><Memberships/></MainLayout>}/>
      <Route path="/games"        element={<MainLayout><Games/></MainLayout>}/>
      <Route path="/wheel"        element={<MainLayout><WheelPage/></MainLayout>}/>
      <Route path="/reviews"      element={<MainLayout><Reviews/></MainLayout>}/>
      <Route path="/deposit"      element={<MainLayout><Deposit/></MainLayout>}/>
      <Route path="/policy/:type" element={<MainLayout><PolicyPage/></MainLayout>}/>
      <Route path="/chat"         element={<ProtectedRoute><MainLayout><ChatPage/></MainLayout></ProtectedRoute>}/>
      <Route path="/profile"      element={<ProtectedRoute><MainLayout><Profile/></MainLayout></ProtectedRoute>}/>
      <Route path="/login"        element={<PublicRoute><Login/></PublicRoute>}/>
      <Route path="/register"     element={<PublicRoute><Register/></PublicRoute>}/>

      <Route path="/admin" element={<AdminRoute><AdminLayout/></AdminRoute>}>
        <Route index              element={<AdminDashboard/>}/>
        <Route path="users"         element={<AdminUsers/>}/>
        <Route path="platforms"     element={<AdminPlatforms/>}/>
        <Route path="offers"        element={<AdminOffers/>}/>
        <Route path="memberships"   element={<AdminMemberships/>}/>
        <Route path="deposits"      element={<AdminDeposits/>}/>
        <Route path="payments"      element={<AdminPayments/>}/>
        <Route path="games"         element={<AdminGames/>}/>
        <Route path="wheel"         element={<AdminWheelSpins/>}/>
        <Route path="announcements" element={<AdminAnnouncements/>}/>
        <Route path="referrals"     element={<AdminReferrals/>}/>
        <Route path="reviews"       element={<AdminReviews/>}/>
        <Route path="chat"          element={<AdminChat/>}/>
        <Route path="policy"        element={<AdminPolicy/>}/>
        <Route path="about"         element={<AdminAbout/>}/>
      </Route>
      <Route path="*" element={<Navigate to="/"/>}/>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <Router>
          <AppRoutes/>
        </Router>
      </SocketProvider>
    </AuthProvider>
  );
}
export default App;
